import { useEffect } from "react";
import { isInputElement } from "@rapid/utils/generalUtils";

interface KeyboardShortcutOptions {
    /**
     * The key(s) to listen for. Can be a single key or array of keys.
     * Matching is case-insensitive, so "a" will match both "a" and "A".
     * Examples: "ArrowLeft", " " (space), "a", ["j", "k"]
     */
    key: string | string[];
    /**
     * The handler function to call when the shortcut is triggered
     */
    handler: () => void;
    /**
     * Whether the shortcut is disabled
     */
    disabled?: boolean;
    /**
     * Whether to require the Meta key (Command on Mac, Ctrl on Windows)
     * When true: only fires WITH Meta/Ctrl
     * When false (default): only fires WITHOUT Meta/Ctrl (prevents conflicts)
     */
    requireMeta?: boolean;
}

/**
 * Custom hook to add keyboard shortcuts with common patterns
 *
 * Automatically:
 * - Prevents firing when typing in input fields
 * - Calls preventDefault on the event
 * - Ignores key repeat events (holding down)
 * - Performs case-insensitive key matching
 *
 * @example
 * // Simple key (case-insensitive)
 * useKeyboardShortcut({ key: "a", handler: approve }); // matches both "a" and "A"
 *
 * @example
 * // Multiple keys for same action
 * useKeyboardShortcut({ key: ["j", "k"], handler: skipBackward });
 *
 * @example
 * // With Command/Ctrl modifier
 * useKeyboardShortcut({
 *   key: "ArrowRight",
 *   handler: advance,
 *   requireMeta: true,
 *   disabled: isLoading
 * });
 */
export function useKeyboardShortcut(options: KeyboardShortcutOptions) {
    const { key, handler, disabled = false, requireMeta = false } = options;

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Ignore key repeats (holding down)
            if (event.repeat) return;

            // Check if the key matches (case-insensitive)
            const keys = Array.isArray(key) ? key : [key];
            const eventKeyLower = event.key.toLowerCase();
            const eventCodeLower = event.code.toLowerCase();
            const keyMatches = keys.some((k) => {
                const kLower = k.toLowerCase();
                return eventKeyLower === kLower || eventCodeLower === kLower;
            });
            if (!keyMatches) return;

            // Check Meta/Ctrl key state
            const hasMetaKey = event.metaKey || event.ctrlKey;
            if (requireMeta) {
                // If Meta is required, only fire when it's pressed
                if (!hasMetaKey) return;
            } else {
                // If Meta is not required, ignore when it's pressed (prevents conflicts)
                if (hasMetaKey) return;
            }

            // Don't fire when typing in input fields
            const target = event.target as HTMLElement | null;
            if (target && isInputElement(target)) return;

            if (disabled) return;

            event.preventDefault();

            handler();
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [key, handler, disabled, requireMeta]);
}
